import React, { useEffect, useState } from "react";
import { Button, Spinner, Table } from "reactstrap";

import { Aviso, EstadoTabela, ModalHub, Pill, fmtNumber } from "../_documentosFrete/comum";
import { emParalelo, gerarEtiqueta, mensagemErroPedido, notasDosPedidos, opcoesEtiqueta } from "../../../services/pedidosApi";
import { rotuloServico } from "../ConsultaNotasFiscais/EtiquetaTab";

// Etiquetas em massa (H5, PO1-2379) sobre a PO1-3378: para cada NF dos pedidos
// selecionados carrega GET /notas-fiscais/consulta/:id/etiqueta (concorrência 3),
// monta a proposta (transportadora/serviço da sugestão, volumes do Pedido ou da
// NF), pede UMA confirmação de cobrança e gera com POST (concorrência 3),
// mostrando o resultado por NF. `modo="retentar"` usa só as NFs com envio em
// falha, com a transportadora/serviço do envio que falhou.

export const CONCORRENCIA = 3;
const ATIVOS = ["criando", "pendente", "emitida"];

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// Volumes do envio: os dados operacionais do Pedido (qtdVolumes/peso informados
// pelo operador) têm prioridade; o peso informado é do pedido inteiro, então é
// dividido pelos volumes. Sem eles, os volumes sugeridos pela NF.
export function volumesDaNota(pedidoOperacional, volumesSugeridos) {
  const po = pedidoOperacional || {};
  const qtd = num(po.qtdVolumes);
  const peso = num(po.peso);
  if ((qtd && qtd > 0) || (peso && peso > 0)) {
    const quantidade = qtd && qtd > 0 ? Math.round(qtd) : 1;
    return [
      {
        quantidade,
        pesoKg: peso && peso > 0 ? Math.round((peso / quantidade) * 1000) / 1000 : null,
        altura: num(po.altura),
        largura: num(po.largura),
        comprimento: num(po.comprimento),
      },
    ];
  }
  return (Array.isArray(volumesSugeridos) ? volumesSugeridos : []).map((v) => ({
    quantidade: num(v.quantidade) || 1,
    pesoKg: num(v.pesoKg),
    altura: num(v.altura),
    largura: num(v.largura),
    comprimento: num(v.comprimento),
  }));
}

// Monta a linha do modal a partir da NF e das opções da 3378 (regra pura).
export function montarLinha(nota, opcoes, modo) {
  const o = opcoes || {};
  const transportadoras = Array.isArray(o.transportadoras) ? o.transportadoras : [];
  const envios = Array.isArray(o.envios) && o.envios.length ? o.envios : Array.isArray(nota.envios) ? nota.envios : [];
  const linha = {
    nota,
    opcoes: o,
    idTransportadora: null,
    servico: null,
    idCotacao: null,
    transportadora: null,
    volumes: volumesDaNota(nota.pedidoOperacional, o.volumesSugeridos),
    incluir: true,
    motivo: null,
    resultado: null,
  };

  if (modo === "retentar") {
    const falhos = envios.filter((e) => e.status === "falha");
    const envio = falhos[falhos.length - 1];
    if (!envio) return { ...linha, incluir: false, motivo: "Nenhum envio com falha nesta nota" };
    linha.idTransportadora = envio.idTransportadora;
    linha.servico = envio.servico || null;
    linha.envioFalho = envio;
  } else {
    const s = o.sugestao;
    if (!s || !s.idTransportadora) return { ...linha, incluir: false, motivo: "Sem sugestão de transportadora: gere pela aba Etiqueta da NF" };
    linha.idTransportadora = s.idTransportadora;
    linha.servico = s.servico || null;
    linha.idCotacao = s.idCotacao || null;
  }
  if (o.sugestao && String(o.sugestao.idTransportadora) === String(linha.idTransportadora)) linha.idCotacao = o.sugestao.idCotacao || null;

  linha.transportadora = transportadoras.find((t) => String(t.idTransportadora) === String(linha.idTransportadora)) || null;
  if (!linha.transportadora) return { ...linha, incluir: false, motivo: "Transportadora não gera etiqueta para esta conta" };
  const pendencias = Array.isArray(linha.transportadora.pendencias) ? linha.transportadora.pendencias : [];
  if (pendencias.length) return { ...linha, incluir: false, motivo: `Cadastro incompleto: ${pendencias.join("; ")}` };
  if (linha.transportadora.conectorKey === "melhor-envio" && !linha.servico) {
    return { ...linha, incluir: false, motivo: "Melhor Envio exige serviço cotado: cote o frete antes" };
  }
  if (!linha.volumes.length || !linha.volumes.every((v) => v.pesoKg > 0)) {
    return { ...linha, incluir: false, motivo: "Sem peso: informe nos dados operacionais do pedido" };
  }
  if (modo !== "retentar" && envios.some((e) => ATIVOS.indexOf(e.status) !== -1)) {
    return { ...linha, incluir: false, motivo: "Já tem etiqueta ativa (marque para gerar outra)" };
  }
  return linha;
}

const servicoDaLinha = (l) => {
  if (!l.servico) return "Serviço do contrato";
  const r = rotuloServico({ idTransportadora: l.idTransportadora, servico: l.servico }, l.opcoes && l.opcoes.propostas);
  return r.codigo && r.nome !== r.codigo ? `${r.nome} (${r.codigo})` : r.nome;
};

const resumoVolumes = (vols) =>
  vols.map((v) => `${v.quantidade}× ${v.pesoKg != null ? String(v.pesoKg).replace(".", ",") : "?"} kg`).join(" · ");

export default function EtiquetasModal({ modo = "gerar", pedidos, embarcadorId, onClose }) {
  const [fase, setFase] = useState("carregando"); // carregando | revisar | gerando | concluido
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [confirmarCobranca, setConfirmarCobranca] = useState(false);
  const [alterou, setAlterou] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setFase("carregando");
      setErro("");
      try {
        let notas = await notasDosPedidos(pedidos);
        if (modo === "retentar") notas = notas.filter((n) => (n.envios || []).some((e) => e.status === "falha"));
        const resultados = await emParalelo(notas, CONCORRENCIA, (n) => opcoesEtiqueta(n.idNota, embarcadorId));
        if (!ativo) return;
        setLinhas(
          notas.map((n, i) => {
            const r = resultados[i];
            if (!r.ok) {
              return { nota: n, opcoes: {}, volumes: [], incluir: false, motivo: mensagemErroPedido(r.erro, "Não foi possível carregar as opções"), resultado: null };
            }
            return montarLinha(n, r.valor, modo);
          })
        );
        setFase("revisar");
      } catch (e) {
        if (!ativo) return;
        setErro(mensagemErroPedido(e, "Não conseguimos carregar as notas dos pedidos."));
        setFase("revisar");
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [pedidos, modo, embarcadorId]);

  const incluidas = linhas.filter((l) => l.incluir);
  const exigeCobranca = incluidas.some((l) => l.transportadora && l.transportadora.exigeConfirmacaoCobranca);
  const transportadorasResumo = [];
  incluidas.forEach((l) => {
    const nome = (l.transportadora && l.transportadora.fantasia) || `#${l.idTransportadora}`;
    const servico = servicoDaLinha(l);
    const chave = `${nome} · ${servico}`;
    const item = transportadorasResumo.find((t) => t.chave === chave);
    if (item) item.qtd += 1;
    else transportadorasResumo.push({ chave, qtd: 1 });
  });

  const alternar = (idNota) => setLinhas((atual) => atual.map((l) => (l.nota.idNota === idNota ? { ...l, incluir: !l.incluir } : l)));

  const podeGerar = fase === "revisar" && incluidas.length > 0 && (!exigeCobranca || confirmarCobranca);

  const gerar = async () => {
    if (!podeGerar) return;
    setFase("gerando");
    await emParalelo(incluidas, CONCORRENCIA, async (l) => {
      let resultado;
      try {
        const data = await gerarEtiqueta(
          l.nota.idNota,
          {
            idTransportadora: Number(l.idTransportadora),
            servico: l.servico || null,
            volumes: l.volumes,
            idCotacao: l.idCotacao || null,
            confirmarCobranca: Boolean(l.transportadora && l.transportadora.exigeConfirmacaoCobranca && confirmarCobranca),
          },
          embarcadorId
        );
        const falhou = data && data.status === "falha";
        resultado = falhou
          ? { tom: "erro", texto: `Falha: ${((data && data.erros) || []).join("; ") || "a transportadora recusou o envio"}` }
          : data && data.dryRun
            ? { tom: "info", texto: "Modo teste: simulada" }
            : data && data.status === "pendente"
              ? { tom: "aviso", texto: "Em processamento na transportadora" }
              : { tom: "ok", texto: `Emitida (${((data && data.envios) || []).length || 1} objeto(s))` };
      } catch (e) {
        const d = (e && e.response && e.response.data) || {};
        resultado = {
          tom: "erro",
          texto: d.pendencias && d.pendencias.length ? `Faltam dados: ${d.pendencias.join("; ")}` : mensagemErroPedido(e, "Não foi possível gerar a etiqueta"),
        };
      }
      setLinhas((atual) => atual.map((x) => (x.nota.idNota === l.nota.idNota ? { ...x, resultado } : x)));
      return resultado;
    });
    setAlterou(true);
    setFase("concluido");
  };

  const fechar = () => {
    if (fase === "gerando") return;
    onClose(alterou);
  };

  const concluidas = linhas.filter((l) => l.resultado);
  const totais = {
    ok: concluidas.filter((l) => l.resultado.tom === "ok" || l.resultado.tom === "info").length,
    pendente: concluidas.filter((l) => l.resultado.tom === "aviso").length,
    falha: concluidas.filter((l) => l.resultado.tom === "erro").length,
  };
  const tomPill = { ok: "success", info: "info", aviso: "warning", erro: "warning" };

  const titulo = modo === "retentar" ? "Re-tentar etiquetas com falha" : "Gerar etiquetas";

  return (
    <ModalHub titulo={`${titulo} · ${fmtNumber((pedidos || []).length)} pedido(s)`} icone="tim-icons icon-tag" onClose={fechar} maxWidth={980}>
      {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
      {fase === "carregando" ? <EstadoTabela loading textoCarregando="Consultando as opções de etiqueta de cada nota..." /> : null}

      {fase !== "carregando" && !erro ? (
        <div className="docs-frete">
          {!linhas.length ? (
            <Aviso tom="aviso">
              {modo === "retentar" ? "Nenhuma nota dos pedidos selecionados tem envio com falha." : "Nenhuma nota encontrada para os pedidos selecionados."}
            </Aviso>
          ) : (
            <>
              <div className="docs-frete__totais" role="region" aria-label="Resumo das etiquetas">
                <span>
                  Notas <b>{fmtNumber(incluidas.length)}</b> de {fmtNumber(linhas.length)}
                </span>
                {transportadorasResumo.map((t) => (
                  <span key={t.chave}>
                    {t.chave} <b>{fmtNumber(t.qtd)}</b>
                  </span>
                ))}
                {fase === "concluido" ? (
                  <span>
                    Resultado{" "}
                    <b>
                      {fmtNumber(totais.ok)} emitida(s) · {fmtNumber(totais.pendente)} em processamento · {fmtNumber(totais.falha)} falha(s)
                    </b>
                  </span>
                ) : null}
              </div>

              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive>
                <thead className="text-primary">
                  <tr>
                    <th className="is-sel" aria-label="Incluir" />
                    <th>Nota fiscal</th>
                    <th>Pedido</th>
                    <th>Transportadora</th>
                    <th>Serviço</th>
                    <th>Volumes</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr key={l.nota.idNota}>
                      <td className="is-sel">
                        <input
                          type="checkbox"
                          aria-label={`Incluir nota ${l.nota.numero}`}
                          checked={l.incluir}
                          disabled={fase !== "revisar" || !l.idTransportadora || !l.transportadora}
                          onChange={() => alternar(l.nota.idNota)}
                        />
                      </td>
                      <td>
                        <strong>{l.nota.numero}</strong>
                        <small>Série {l.nota.serie || "-"}</small>
                      </td>
                      <td>{l.nota.pedido}</td>
                      <td>{(l.transportadora && l.transportadora.fantasia) || (l.idTransportadora ? `#${l.idTransportadora}` : "-")}</td>
                      <td>{l.idTransportadora ? servicoDaLinha(l) : "-"}</td>
                      <td>{l.volumes.length ? resumoVolumes(l.volumes) : "-"}</td>
                      <td>
                        {l.resultado ? (
                          <Pill tom={tomPill[l.resultado.tom] || "info"} title={l.resultado.texto}>
                            {l.resultado.texto}
                          </Pill>
                        ) : l.motivo ? (
                          <span className="docs-frete__dica" style={{ marginTop: 0 }}>
                            {l.motivo}
                          </span>
                        ) : (
                          <Pill tom="success">Pronta para gerar</Pill>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {exigeCobranca && fase === "revisar" ? (
                <label className="pedidos__cobranca">
                  <input type="checkbox" checked={confirmarCobranca} onChange={(e) => setConfirmarCobranca(e.target.checked)} />
                  Estou ciente de que gerar estas etiquetas debita a carteira do embarcador na transportadora.
                </label>
              ) : null}
              {fase === "gerando" ? (
                <Aviso tom="info">
                  <Spinner size="sm" style={{ marginRight: 6 }} /> Gerando {fmtNumber(incluidas.length)} etiqueta(s), {CONCORRENCIA} por vez...
                </Aviso>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={fase === "gerando"}>
          Fechar
        </Button>
        {fase === "revisar" || fase === "gerando" ? (
          <Button color="primary" size="sm" onClick={gerar} disabled={!podeGerar}>
            {fase === "gerando" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
            {fase === "gerando" ? "Gerando..." : `${modo === "retentar" ? "Re-tentar" : "Gerar"} ${fmtNumber(incluidas.length)} etiqueta(s)`}
          </Button>
        ) : null}
      </div>
    </ModalHub>
  );
}
